import { Link } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { useLang, useLp } from "../lib/i18n";
import { getPosts } from "../lib/blog";
import { blogIndexCopy } from "../lib/translations/pages/blog";

const DATE_LOCALE: Record<string, string> = {
  en: "en-US",
  pt: "pt-BR",
  es: "es-ES",
};

export function Blog() {
  const lang = useLang();
  const lp = useLp();
  const t = blogIndexCopy[lang];
  const posts = getPosts(lang);
  const dateFormatter = new Intl.DateTimeFormat(DATE_LOCALE[lang] ?? "en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="min-h-screen flex flex-col">
      <Seo title={t.seo.title} description={t.seo.description} path="/blog" />
      <Header />

      <main className="flex-1">
        {/* Hero */}
        <section className="border-b border-margin dark:border-paper/10">
          <div className="mx-auto max-w-3xl px-6 pt-20 pb-14 sm:pt-28 sm:pb-16">
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink/55 dark:text-paper/55">
              {t.eyebrow}
            </p>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-ink dark:text-paper">
              {t.heading}
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-ink/75 dark:text-paper/75">
              {t.intro}
            </p>
          </div>
        </section>

        {/* List */}
        <section>
          <div className="mx-auto max-w-5xl px-6 py-16">
            {posts.length === 0 ? (
              <p className="text-[15px] text-ink/60 dark:text-paper/60">{t.empty}</p>
            ) : (
              <ul className="grid gap-6 lg:grid-cols-2">
                {posts.map((p) => (
                  <Reveal key={p.slug}>
                    <li className="h-full overflow-hidden rounded-2xl border border-ink/10 bg-paper shadow-sm shadow-ink/5 dark:border-paper/10 dark:bg-paper/5 dark:shadow-black/20">
                      <Link
                        to={lp + "/blog/" + p.slug}
                        className="group flex h-full flex-col"
                      >
                        {(p.hero?.src || p.frontmatter.ogImage) && (
                          <div className="overflow-hidden border-b border-ink/10 bg-margin/40 dark:border-paper/10 dark:bg-paper/6">
                            <img
                              src={p.hero?.src || p.frontmatter.ogImage}
                              alt={p.hero?.alt || p.frontmatter.title}
                              width={p.hero?.width || 1200}
                              height={p.hero?.height || 630}
                              loading="lazy"
                              className="aspect-[16/9] w-full object-cover transition duration-300 group-hover:scale-[1.03]"
                            />
                          </div>
                        )}
                        <div className="flex flex-1 flex-col p-6">
                          <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-graphite dark:text-paper/60">
                            {p.category}
                            <span className="px-2 text-ink/35 dark:text-paper/35">·</span>
                            <time dateTime={p.frontmatter.date}>
                              {dateFormatter.format(new Date(p.frontmatter.date))}
                            </time>
                            <span className="px-2 text-ink/35 dark:text-paper/35">·</span>
                            {p.readingMinutes} min
                          </p>
                          <h2 className="mt-4 font-display text-xl font-semibold tracking-tight text-ink dark:text-paper group-hover:underline decoration-ink/30 dark:decoration-paper/30 sm:text-2xl sm:leading-tight">
                            {p.frontmatter.title}
                          </h2>
                          <p className="mt-4 text-[15px] leading-relaxed text-ink/70 dark:text-paper/70">
                            {p.frontmatter.description}
                          </p>
                          <div className="mt-6 flex flex-wrap gap-2">
                            {(p.frontmatter.tags ?? []).slice(0, 3).map((tag) => (
                              <span
                                key={tag}
                                className="rounded-full border border-ink/10 px-3 py-1 text-[12px] text-ink/55 dark:border-paper/10 dark:text-paper/55"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                          <p className="mt-auto pt-6 text-[13px] text-ink/60 dark:text-paper/60">
                            {t.readMore}
                          </p>
                        </div>
                      </Link>
                    </li>
                  </Reveal>
                ))}
              </ul>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
